import {
  HasTags,
  Maybe,
  MaybeArray,
  Replace,
  Tag,
  Tags,
} from "@examspark/types";

import { filterMaybeArray } from "../utils";

export const foundInArray = (
  findIndexResult: number | undefined,
): findIndexResult is number => {
  if (findIndexResult === -1 || findIndexResult === undefined) {
    return false;
  }
  return true;
};

/**
 * Put a tag on a resource if the tag doesn't exist. This prevents multiple tags
 * with the same key from being added twice.
 */
export const putTagValueForResource = (
  resource: NonNullable<HasTags>,
  tag: Tag,
) => {
  const foundIndex = resource?.tags?.findIndex((_tag) => _tag?.key === tag.key);

  if (!foundInArray(foundIndex)) {
    addTagToResource(resource, tag);
    return;
  }

  if (!resource?.tags?.[foundIndex]) return;
  resource.tags[foundIndex] = tag;
};

export const setTagValueForResource = (
  resource: HasTags,
  key: string,
  value: string | null,
) => {
  if (resource == null) return;
  if (resource.tags == null) resource.tags = [];

  const foundIndex = resource.tags.findIndex((_tag) => _tag?.key === key);

  if (foundIndex < 0) {
    return addTagToResource(resource, { key, value, type: "string" });
  }

  // can't be null, we already validated its existence with findIndex
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  resource.tags[foundIndex]!.value = value;
  return true;
};

export const addTagToResource = (
  resource: NonNullable<HasTags>,
  tag: Tag,
  allowMultiple = false,
) => {
  if (resource.tags == null) resource.tags = [];
  return addTagToTags(resource.tags, tag, allowMultiple);
};

export const addTagToTags = (
  tags: NonNullable<Tags>,
  tag: Tag,
  allowMultiple = false,
) => {
  const found = tags?.find(
    (_tag) =>
      _tag?.key === tag.key && (_tag?.value === tag.value || !allowMultiple),
  );

  if (found) return false;

  tags.push(tag);
  return true;
};

export const removeTagFromResource = (
  resource: NonNullable<HasTags>,
  tag: Tag | string,
) => removeTagFromTags(resource.tags, tag);

export const removeTagFromTags = (tags: Tags, tag: Tag | string) => {
  if (tags == null) return false;

  let foundIndex: number;
  let removed = false;

  do {
    foundIndex = tags?.findIndex((_tag) => {
      if (typeof tag === "string") {
        return _tag?.key === tag;
      } else if (tag.value !== undefined) {
        return _tag?.key === tag.key && _tag?.value === tag.value;
      } else {
        return _tag?.key === tag.key;
      }
    });

    if (foundIndex !== -1) {
      tags?.splice(foundIndex, 1);
      removed = true;
    }
  } while (foundIndex !== -1);

  return removed;
};

export const getTagFromResource = <TValue extends string = string>(
  resource: HasTags,
  key: string,
  value?: TValue,
) => getTagFromTags<TValue>(filterMaybeArray(resource?.tags), key, value);

export const getTagFromTags = <TValue extends string = string>(
  tags: Tags,
  key: string,
  value?: TValue,
) => {
  return tags?.find(
    (_tag) =>
      _tag?.key === key && (_tag.value === value || value === undefined),
  ) as Replace<Tag, { value: TValue }> | undefined;
};

export const getUniqueTags = (tags?: Maybe<Tag>[]) => {
  if (!tags) return [];

  const uniqueTags: Tag[] = [];
  const seen = new Set<string>();

  for (const tag of tags) {
    if (tag && tag.key != null && tag.value != null) {
      const tagKey = `${tag.key}:${tag.value}`;
      if (!seen.has(tagKey)) {
        seen.add(tagKey);
        uniqueTags.push(tag);
      }
    }
  }

  return uniqueTags;
};

export const getTagValueFromResource = (resource: HasTags, key: string) =>
  getTagFromResource(resource, key)?.value;
export const getTagValueFromTags = (tags: Tags, key: string) =>
  getTagFromTags(tags, key)?.value;

export const getAllTagsFromResource = <TValue extends string = string>(
  resource: HasTags,
  key: string,
) => getAllTagsFromTags<TValue>(resource?.tags, key);

export const getAllTagsFromTags = <TValue extends string = string>(
  tags: Tags,
  key: string,
) =>
  (tags?.filter((t) => t?.key === key) ?? []) as Array<
    Replace<Tag, { value: TValue }>
  >;

export const getAllTagValuesFromResource = (resource: HasTags, key: string) =>
  getAllTagsFromResource(resource, key).map((t) => t.value);
export const getAllTagValuesFromTags = (tags: Tags, key: string) =>
  getAllTagsFromTags(tags, key).map((t) => t.value);

export const hasTag = (
  resourceOrTags: Tags | HasTags,
  key: string,
  value?: string,
) => {
  let tags: Array<Tag | null | undefined> | undefined;
  if (Array.isArray(resourceOrTags)) {
    tags = resourceOrTags;
  } else {
    tags = filterMaybeArray(resourceOrTags?.tags);
  }
  return getTagFromTags(tags, key, value) != null;
};

export const extractByTag = <TItem extends HasTags>(
  items: MaybeArray<TItem>,
  key: string,
  value: string,
) =>
  filterMaybeArray(items)?.filter(
    (item) =>
      filterMaybeArray(item?.tags).find(
        (tag) => tag.key === key && tag.value === value,
      ) != null,
  );
